import { useMemo } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "shared/ui/tabs"
import { Badge } from "shared/ui/badge"
import { cn } from "shared/ui/utils"
import { Info, MapPin, User } from "lucide-react"
import type { WorkerSelectionItem } from "modules/permit"
import type { SearchSelectValue } from "./SearchSelectField"
import type { TemplateConfiguration } from "shared/api/templateService"
import type { WorkAreaSelection } from "./WorkAreaSection"
import type { UploadedFileItem } from "./RequiredDocumentsSection"
import { getPermitStatusLabel } from "modules/permit/constants/permitStatus"
import { usePermitRequestActionsContext } from "modules/permit/context/PermitRequestActionsContext"
import { buildAttachmentUrl } from "shared/utils/attachmentUrl"
import { SummaryOverview } from "./permit-request/summary/SummaryOverview"
import { DocumentsTab } from "./permit-request/summary/DocumentsTab"
import { WorkersTab } from "./permit-request/summary/WorkersTab"

type RequiredDocumentSummary = {
  id: string
  name: string
  code: string
  isMandatory: boolean
}

type DocumentAttachment = {
  id: string
  name: string
  url?: string | null
}

type PermitSummaryData = {
  permitNo: string
  permitType: string
  permitTypeCode: string
  workDescription: string
  requestorName: string
  requestorCompany: string
  workLocation: string
  unit: string
  building: string
  floor: string
  room: string
  other: string
  startDate: string
  endDate: string
  startTime: string
  endTime: string
  controller: SearchSelectValue | null
}

type RiskSnapshot = {
  level: string
  riskScore: number
  consequenceScore: number
  likelihoodScore: number
}

export type SummaryActionNotice = {
  type: "cancel" | "revision" | "request-info" | "change-work-time" | "extend-permit"
  title: string
  message?: string
  actor?: string
  timestamp?: string
}

interface PermitRequestSummaryPanelProps {
  className?: string
  permitData: PermitSummaryData
  selectedWorkers: WorkerSelectionItem[]
  selectedActivities: string[]
  requiredDocuments: RequiredDocumentSummary[]
  uploadedFiles: UploadedFileItem[]
  riskInfo: RiskSnapshot
  workAreaSelection: WorkAreaSelection
  templateConfig: TemplateConfiguration | null
  permitStatusCode?: string | null
  permitStatusName?: string | null
  generalFieldLabels?: Record<string, string>
  actionNotice?: SummaryActionNotice | null
}

const formatTextValue = (value?: string | null, fallback = "Not provided"): string => {
  if (typeof value !== "string") {
    return fallback
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : fallback
}

const buildFileUrl = (path?: string | null): string | null => buildAttachmentUrl(path)

const formatDateValue = (value?: string | null): string | null => {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }
  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })
}

const formatDateRange = (start?: string | null, end?: string | null): string => {
  const startLabel = formatDateValue(start)
  const endLabel = formatDateValue(end)
  if (startLabel && endLabel) {
    return `${startLabel} - ${endLabel}`
  }
  return startLabel ?? endLabel ?? "Not provided"
}

const normalizeTimeTo24 = (time?: string | null): string | null => {
  if (!time) return null
  let timeText = time.trim()
  if (!timeText) return null

  let detectedPeriod = ""
  const ampmMatch = timeText.match(/\s*(AM|PM)$/i)
  if (ampmMatch) {
    detectedPeriod = ampmMatch[1].toUpperCase()
    timeText = timeText.replace(/\s*(AM|PM)$/i, "").trim()
  }

  const [rawHours, rawMinutes = "0"] = timeText.split(":")
  let hours = Number(rawHours)
  let minutes = Number(rawMinutes)
  if (Number.isNaN(hours)) hours = 0
  if (Number.isNaN(minutes)) minutes = 0

  if (detectedPeriod === "PM" && hours < 12) {
    hours += 12
  } else if (detectedPeriod === "AM" && hours === 12) {
    hours = 0
  }

  const hh = String(Math.max(0, Math.min(23, hours))).padStart(2, "0")
  const mm = String(Math.max(0, Math.min(59, minutes))).padStart(2, "0")
  return `${hh}:${mm}`
}

const formatTimeRange = (
  startTime?: string | null,
  endTime?: string | null,
): string => {
  const startLabel = normalizeTimeTo24(startTime)
  const endLabel = normalizeTimeTo24(endTime)
  if (startLabel && endLabel) {
    return `${startLabel} - ${endLabel}`
  }
  return startLabel ?? endLabel ?? "Not provided"
}

const buildLocationLabel = (data: PermitSummaryData): string | null => {
  const parts = [data.workLocation, data.unit, data.building, data.floor, data.room, data.other]
    .map((value) => value?.trim())
    .filter((value) => value && value.length > 0) as string[]

  return parts.length > 0 ? parts.join(" / ") : null
}

export function PermitRequestSummaryPanel({
  className,
  permitData,
  selectedWorkers,
  requiredDocuments,
  uploadedFiles,
  workAreaSelection,
  permitStatusCode,
  permitStatusName,
  generalFieldLabels = {},
  actionNotice,
}: PermitRequestSummaryPanelProps) {
  const {
    isSaving,
    saveMessage,
    saveError,
    isSubmittingWorkflow,
    submitMessage,
    submitError,
  } = usePermitRequestActionsContext()

  const controllerLabel = formatTextValue(permitData.controller?.label, "Not assigned")

  const documentsWithStatus = useMemo(() => {
    const resolveFileName = (file: UploadedFileItem): string => {
      const candidate =
        file.name?.trim() ||
        file.fileName?.trim() ||
        file.storedFileName?.trim() ||
        file.documentCode?.trim()
      return candidate || "ไฟล์แนบ"
    }

    const filesByDocId = new Map<string, DocumentAttachment[]>()
    uploadedFiles.forEach((file) => {
      const docId = file.docId?.trim().toLowerCase()
      if (!docId) {
        return
      }
      const attachments = filesByDocId.get(docId) ?? []
      attachments.push({
        id: file.id,
        name: resolveFileName(file),
        url: buildFileUrl(file.storagePath),
      })
      filesByDocId.set(docId, attachments)
    })

    return requiredDocuments.map((doc) => {
      const docId = doc.id?.trim().toLowerCase()
      const files = docId ? filesByDocId.get(docId) ?? [] : []
      const uploaded = files.length > 0
      return {
        ...doc,
        uploaded,
        files,
      }
    })
  }, [requiredDocuments, uploadedFiles])

  const uploadedCount = documentsWithStatus.filter((doc) => doc.uploaded).length
  const totalDocuments = documentsWithStatus.length
  const missingMandatory = documentsWithStatus.filter((doc) => doc.isMandatory && !doc.uploaded).length

  const fallbackLocationLabel = buildLocationLabel(permitData)
  const resolvedWorkLocationLabel = generalFieldLabels.WORKLOCATION?.trim() || ""
  const resolvedUnitLabel = generalFieldLabels.UNIT?.trim() || ""
  const rawWorkLocation = permitData.workLocation?.trim() || ""
  const rawUnit = permitData.unit?.trim() || ""
  const areaParts = [resolvedWorkLocationLabel || rawWorkLocation, resolvedUnitLabel || rawUnit]
    .map((value) => value?.trim())
    .filter((value) => value && value.length > 0) as string[]

  const gpsLabel =
    typeof workAreaSelection.gpsLatitude === "number" && typeof workAreaSelection.gpsLongtitude === "number"
      ? `${workAreaSelection.gpsLatitude.toFixed(6)}, ${workAreaSelection.gpsLongtitude.toFixed(6)}`
      : null

  const workAreaFallback =
    workAreaSelection.mapMode === "gps" ? gpsLabel : workAreaSelection.plotPlanName?.trim() || null
  const areaDisplay = areaParts.join(" / ") || workAreaFallback || fallbackLocationLabel || "Not assigned"

  const statusLabel = getPermitStatusLabel(permitStatusCode, permitStatusName)
  const noticeIconClass =
    actionNotice?.type === "cancel"
      ? "text-red-500"
      : actionNotice?.type === "revision"
        ? "text-red-500"
        : actionNotice?.type === "request-info"
          ? "text-orange-500"
        : actionNotice?.type === "change-work-time"
          ? "text-amber-500"
          : actionNotice?.type === "extend-permit"
            ? "text-blue-500"
          : ""
  const noticeBgClass =
    actionNotice?.type === "cancel"
      ? "border-red-200 bg-red-50"
      : actionNotice?.type === "revision"
        ? "border-red-200 bg-red-50"
        : actionNotice?.type === "request-info"
          ? "border-orange-200 bg-orange-50"
        : actionNotice?.type === "change-work-time"
          ? "border-amber-200 bg-amber-50"
          : actionNotice?.type === "extend-permit"
            ? "border-blue-200 bg-blue-50"
          : "border-gray-200 bg-gray-50"

  const actionStatus = useMemo(() => {
    if (submitError || saveError) {
      return { label: submitError ?? saveError ?? "เกิดข้อผิดพลาด", variant: "destructive" as const }
    }
    if (isSubmittingWorkflow) {
      return { label: "กำลังส่งอนุมัติ...", variant: "secondary" as const }
    }
    if (isSaving) {
      return { label: "กำลังบันทึก...", variant: "secondary" as const }
    }
    if (submitMessage) {
      return { label: submitMessage, variant: "outline" as const }
    }
    if (saveMessage) {
      return { label: saveMessage, variant: "outline" as const }
    }
    return null
  }, [
    isSaving,
    saveMessage,
    saveError,
    isSubmittingWorkflow,
    submitMessage,
    submitError,
  ])

  return (
    <div className={cn("min-h-0", className)}>
      <Card className="flex h-full flex-col overflow-hidden border-0 shadow-lg ring-1 ring-gray-100">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-gray-900">Live Summary</CardTitle>
            <Badge variant="outline" className="text-xs text-muted-foreground">
              อัปเดตอัตโนมัติ
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            สรุปข้อมูลใบอนุญาตแบบเรียลไทม์ตามข้อมูลที่กรอกด้านซ้าย
          </p>
          {actionStatus ? (
            <Badge variant={actionStatus.variant} className="text-xs font-medium">
              {actionStatus.label}
            </Badge>
          ) : null}
          {actionNotice ? (
            <div className={cn("mt-3 rounded-md border p-3 text-sm", noticeBgClass)}>
              <div className="flex items-start gap-2">
                <div className="pt-0.5">
                  <Info className={cn("h-4 w-4", noticeIconClass || "text-gray-500")} />
                </div>
                <div className="space-y-1">
                  <p className="font-semibold text-gray-900">{actionNotice.title}</p>
                  {actionNotice.message ? <p className="text-gray-700">{actionNotice.message}</p> : null}
                  {actionNotice.actor || actionNotice.timestamp ? (
                    <p className="text-xs text-gray-600">
                      {actionNotice.actor ? <span>โดย {actionNotice.actor}</span> : null}
                      {actionNotice.actor && actionNotice.timestamp ? <span> • </span> : null}
                      {actionNotice.timestamp ? <span>{actionNotice.timestamp}</span> : null}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col overflow-y-auto pt-0">
          <Tabs defaultValue="summary" className="flex min-h-0 flex-1 flex-col gap-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="summary">Summary</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
              <TabsTrigger value="workers">Workers</TabsTrigger>
            </TabsList>

            <TabsContent value="summary" className="space-y-4 pt-0">
              <SummaryOverview permitType={permitData.permitType} statusLabel={statusLabel} />

              <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-500">Permit No</span>
                  <span className="font-semibold text-gray-900">{formatTextValue(permitData.permitNo)}</span>
                </div>

                <div className="mt-4 space-y-2">
                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-600">
                    <User className="h-4 w-4 text-gray-400" />
                    <span>Document Creator</span>
                  </div>
                  <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
                    <div className="flex items-center justify-between">
                      <span className="text-blue-700">Name</span>
                      <span className="font-semibold">
                        {formatTextValue(permitData.requestorName)}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-blue-700">Company</span>
                      <span className="font-semibold">
                        {formatTextValue(permitData.requestorCompany)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500">Work Description</span>
                    <span className="font-semibold text-gray-900">
                      {formatTextValue(permitData.workDescription)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500">Work Date</span>
                    <span className="font-semibold text-gray-900">
                      {formatDateRange(permitData.startDate, permitData.endDate)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500">Working Time</span>
                    <span className="font-semibold text-gray-900">
                      {formatTimeRange(
                        permitData.startTime,
                        permitData.endTime,
                      )}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500">Controller</span>
                    <span className="font-semibold text-gray-900">{controllerLabel}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 text-xs font-semibold text-gray-600">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span>Work Area</span>
                    </div>
                    <span className="text-right font-semibold text-gray-900">{areaDisplay}</span>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="documents">
              <DocumentsTab
                documentsWithStatus={documentsWithStatus}
                uploadedCount={uploadedCount}
                totalDocuments={totalDocuments}
                missingMandatory={missingMandatory}
              />
            </TabsContent>

            <TabsContent value="workers">
              <WorkersTab selectedWorkers={selectedWorkers} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}

export type { PermitSummaryData, RiskSnapshot, RequiredDocumentSummary }
